import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { AdminCode } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Button, type SxProps, type Theme, Typography } from '@mui/material';

import { CopyableCode } from './CopyableCode';

const strings = appStrings.modules.rostersAdmin;

/**
 * What a line KNOWS about its code. Three states, not the two A4 describes: moving the code onto
 * a per-line read adds "not yet known", and "No Active Code" is a claim about the line rather
 * than about the read — made while the read is in flight it says there is none when nobody has
 * looked, and the revoke door is withheld on the strength of it.
 */
export type LineCodeState = 'unavailable' | 'loading' | 'present' | 'absent';

/**
 * One computation, so the cell and the action menu cannot disagree about the same line, and so
 * the precedence is a value rather than a render's branch order — which no type or lint check
 * would have defended. A read that failed is settled, so failure outranks loading.
 *
 * PRESENCE IS MONOTONIC AND ABSENCE IS NOT. A row the decoder dropped cannot un-find a code that
 * was found, so a present code stands over an incomplete list. It CAN hide one, so "this line
 * has no code" is only sayable over a list that arrived whole — otherwise the page asserts the
 * opposite of the truth and withholds the revoke door for a code that still works.
 */
export const lineCodeState = (
  code: AdminCode | null,
  isLoading: boolean,
  isError: boolean,
  isComplete: boolean,
): LineCodeState => {
  if (isError) return 'unavailable';
  if (isLoading) return 'loading';
  if (code?.value) return 'present';

  return isComplete ? 'absent' : 'unavailable';
};

type LineCodeProps = Readonly<{
  id: string;
  code: AdminCode | null;
  state: LineCodeState;
  isCreating: boolean;
  onCreate: () => void;
}>;

export const LineCode = ({ id, code, state, isCreating, onCreate }: LineCodeProps) => {
  switch (state) {
    case 'unavailable':
      return (
        <Typography id={id} variant="text-sm-regular" role="alert" sx={mutedSx}>
          {strings.codeState.unavailable}
        </Typography>
      );
    case 'loading':
      return (
        <Typography id={id} variant="text-sm-regular" role="status" sx={mutedSx}>
          {strings.codeState.loading}
        </Typography>
      );
    case 'present':
      return <CopyableCode id={id} value={code?.value ?? ''} />;
    default:
      return (
        <Button
          id={id}
          hierarchy="Tertiary color"
          size="sm"
          disabled={isCreating}
          onClick={onCreate}
          sx={createSx}
        >
          {isCreating ? strings.actions.creatingCode : strings.actions.createCode}
        </Button>
      );
  }
};

const createSx: SxProps<Theme> = ({ backrs }) => ({
  marginLeft: `-${backrs.spacing['spacing-md']}`,
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
